import { VelocityClient, User } from '@velocity-exchange/sdk';
import { timedPromise } from '@velocity-exchange/common';
import { dlog } from 'src/dev';

const TX_PREP_TIME_METRIC_START_TIME_CACHE = new Map<string, number>();
import {
	DriftClientTxSignedHandler,
	PREVIOUS_TX_SIGNED_HANDLER,
	TxMetricsOpts,
} from '../types';

export const updateAfterTx = (driftClient: VelocityClient, user: User) => {
	setTimeout(() => {
		user.fetchAccounts();
		driftClient.fetchAccounts();
	}, 400);
};

export async function sendTxAndRecordConfirmationTime<
	T extends Promise<unknown>,
>(
	driftClient: VelocityClient,
	txPromise: T,
	onSigned?: DriftClientTxSignedHandler,
	opts?: TxMetricsOpts
) {
	let handler: DriftClientTxSignedHandler;
	const signaturePromise = new Promise<void>((resolve) => {
		handler = (signedTxs) => {
			resolve();
			onSigned?.(signedTxs);
		};
		addTxSignedEventHandler(driftClient, handler);
	});
	const txSignedEventEmitter = driftClient.metricsEventEmitter;

	const txSignatureTimePromise = timedPromise(signaturePromise);
	const txConfirmationTimePromise = timedPromise(txPromise);

	txPromise.catch(() => {
		removeTxSignedEventHandler(driftClient, handler);
	});

	if (opts?.measurePreSignTime) {
		let handler: () => void;
		const preSignPromise = new Promise<void>((resolve) => {
			handler = resolve;
			txSignedEventEmitter.on('preTxSigned', handler);
		});
		const preSignTimePromise = timedPromise(preSignPromise);
		preSignTimePromise
			.then((result) => {
				const cachedStartTime = TX_PREP_TIME_METRIC_START_TIME_CACHE.get(
					opts.measurePreSignTimeMetricKey ?? ''
				);
				const shouldAdjustMeasuredTime = !!cachedStartTime;

				const metricTime = shouldAdjustMeasuredTime
					? Date.now() - cachedStartTime
					: result.promiseTime;

				const metricKey = opts.measurePreSignTimeMetricKey ?? 'unlabelled';

				dlog(`ui_metrics`, `pre_sign_time_measurement ${metricTime}ms`, {
					metricKey,
					promiseTime: result.promiseTime,
					metricTime: metricTime,
					delta: metricTime - result.promiseTime,
					shouldAdjustMeasuredTime,
				});
			})
			.finally(() => {
				if (handler) {
					txSignedEventEmitter.off('preTxSigned', handler);
				}
			});
	}

	Promise.all([txSignatureTimePromise, txConfirmationTimePromise]).then(
		(results) => {
			const txSignatureTime = results[0].promiseTime;
			const txConfirmationTime = results[1].promiseTime;

			const actualConfirmationTime = txConfirmationTime - txSignatureTime;

			if (actualConfirmationTime <= 0) {
				// Caught an edge case once where the txConfirmationTime was less than the txSignatureTime. Don't see how this is possible not how we can "gracefully" handle it, so just going to skip logging this transaction time.
				dlog(`ui_metrics`, `bug::confirmation_time_less_than_sig_time`, {
					txSignatureTime,
					txConfirmationTime,
					diff: actualConfirmationTime,
				});
				return;
			}

			const formattedPromiseTime = +(actualConfirmationTime / 1000).toFixed(1);

			dlog(
				`ui_metrics`,
				`confirmation_time_measurement ${formattedPromiseTime}s`,
				{
					txSignatureTime,
					txConfirmationTime,
					diff: actualConfirmationTime,
				}
			);

			removeTxSignedEventHandler(driftClient, handler);
		}
	);

	return txPromise;
}

const removeTxSignedEventHandler = (
	driftClient: VelocityClient,
	handler: DriftClientTxSignedHandler
) => {
	driftClient.metricsEventEmitter.off('txSigned', handler);
	PREVIOUS_TX_SIGNED_HANDLER.current = undefined;
};

const addTxSignedEventHandler = (
	driftClient: VelocityClient,
	handler: DriftClientTxSignedHandler
) => {
	if (PREVIOUS_TX_SIGNED_HANDLER.current) {
		removeTxSignedEventHandler(driftClient, PREVIOUS_TX_SIGNED_HANDLER.current);
	}

	driftClient.metricsEventEmitter.on('txSigned', handler);
	PREVIOUS_TX_SIGNED_HANDLER.current = handler;
};
